/**
 * Labels for the catalogue reason codes the Admin API returns.
 *
 * The backend decides every state and reason (UT system Services/Catalogue);
 * this file only names them. An unknown code is shown as-is rather than
 * hidden, so a reason added on the server is never silently lost.
 */

const REASONS: Record<string, string> = {
  // Visibility
  DenominationInactive: 'Denomination inactive',
  BrandInactive: 'Brand inactive',
  CategoryInactive: 'Category inactive',
  RegionInactive: 'Region inactive',
  CountryInactive: 'Country inactive',
  NotOffered: 'Not offered in this country',
  // Sellability
  NotVisible: 'Not visible',
  NoProvider: 'No provider mapped',
  ProviderInactive: 'Provider inactive',
  ProductIdentifierMissing: 'Product id missing on the mapping',
  ProviderCurrencyMissing: 'Provider currency missing',
  FxMissing: 'Exchange rate missing',
  FxStale: 'Exchange rate out of date',
  ProviderBalanceInsufficient: 'Provider balance too low',
  ProviderDeprecated: 'Provider integration deprecated',
  // One provider's eligibility (provider availability)
  MappingInactive: 'Mapping inactive',
  BalanceInsufficient: 'Provider balance too low',
  PricingConfigurationMissing: 'No pricing for this country',
  // Blocked changes
  DenominationWithoutProvider: 'No active provider — map one before offering it',
};

const ATTENTION: Record<string, string> = {
  BrandInactive: 'Brand is inactive',
  CategoryInactive: 'Category is inactive on the storefront',
  NoActiveDenominations: 'No active denominations',
  DenominationsWithoutProvider: 'Active denominations without a provider',
  CountryInactive: 'Country is inactive',
  CountryWithoutPricing: 'No active pricing for the country',
  NotOfferedInCountry: 'Denominations not offered',
  CostSourceBlockedInCountry: 'Offered, but every provider is blocked',
  NotSellableInCountry: 'Offered, but customers cannot buy',
  ZeroCostMappings: 'Provider mappings with a zero cost',
  MissingArabicName: 'Arabic name missing',
  MissingImage: 'Logo missing',
  DenominationsMissingImage: 'Active denominations without an image',
};

const WARNINGS: Record<string, string> = {
  NothingSellable: 'Nothing will become purchasable — other reasons still hold these items back.',
  SomeWithoutProvider:
    'Some items have no active provider: customers will see them but cannot buy them.',
};

export function reasonLabel(code: string): string {
  return REASONS[code] ?? code;
}

export function attentionLabel(code: string): string {
  return ATTENTION[code] ?? code;
}

export function warningLabel(code: string): string {
  return WARNINGS[code] ?? code;
}

/** How serious an attention code is: danger means customers cannot buy something. */
export type AttentionSeverity = 'danger' | 'warning' | 'info' | 'neutral';

const ATTENTION_SEVERITY: Record<string, AttentionSeverity> = {
  BrandInactive: 'danger',
  NoActiveDenominations: 'danger',
  DenominationsWithoutProvider: 'danger',
  CountryInactive: 'danger',
  CountryWithoutPricing: 'danger',
  NotSellableInCountry: 'danger',
  CostSourceBlockedInCountry: 'danger',
  ZeroCostMappings: 'warning',
  CategoryInactive: 'warning',
  NotOfferedInCountry: 'info',
  MissingArabicName: 'neutral',
  MissingImage: 'neutral',
  DenominationsMissingImage: 'neutral',
};

export function attentionSeverity(code: string): AttentionSeverity {
  return ATTENTION_SEVERITY[code] ?? 'warning';
}
